import { graphql } from "gatsby";
import React, { useContext } from "react";
import { LayoutContainer } from "../containers/layout";
import PageHeadMeta from "../components/global/page-head-meta";
import { Chatbot, SectionWrapper } from "../components/sections";
import { ComparisonTable } from "../components/sections/compare-table";
import PortableText from "../components/portableText";
import { SiteContext } from "../components/global/site-context";
import { mapEdgesToNodes, toPlainText } from "../lib/helpers";
import { DemoUX } from "../components/sections/demo-ux";
import { Expandable } from "../components/expandable";
import { LogoCarousel } from "../components/sections/logo-carousel";
import { Carousel } from "../components/sections/snowflake/carousel";
import { CardContext } from "../components/card/context";
import { Card } from "../components/card/card";
import { SolutionsSections } from "./offering";
import { PillHeader } from "../components/sections/wrapper";
import { EyebrowPill } from "../components/serializers";
import { typesMap } from "../components/sections/resources-list";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown } from "@fortawesome/free-solid-svg-icons";
import { DEMO_PHRASE } from "../components/sections/snowflake/navigation";
import { Button } from "../components/button";
import { DEFAULT_COPILOT, DEFAULT_WORKFLOW } from "./agent";

const PAGE_WIDTH = null;
const DEFAULT_FIRST_MESSAGE = "What GTM plays are you dreaming up?";
const MAX_STORIES = 16;

export const query = graphql`
  query CompetitorTemplateQuery($id: String!) {
    doc: sanityCompetitor(id: { eq: $id }) {
      _type
      _id
      title
      _rawHero(resolveReferences: { maxDepth: 5 })
      slug {
        current
      }
      _rawExcerpt(resolveReferences: { maxDepth: 5 })
      _rawCompareIntro(resolveReferences: { maxDepth: 5 })
      # heroImage {
      #   ...SanityImageSimple
      # }
      page {
        ... on SanityPage {
          _type
          id
          _id
          sections {
            ... on SanityPageSectionSimple {
              _type
              _rawContent(resolveReferences: { maxDepth: 5 })
              anchor
              header
              _rawSummary(resolveReferences: { maxDepth: 5 })
              layout
              background
              padding
              container
              mainImage {
                ...SanityImageSimple
              }
              image {
                ...SanityImage
                alt
                caption
                link
              }
              tableOfContents
              rounded
              constrainImages
            }
            ... on SanityPageSectionColumns {
              _type
              content {
                header
                mainImage {
                  ...SanityImageSimple
                }
                _rawContent(resolveReferences: { maxDepth: 5 })
                author {
                  ...SanityAuthor
                }
                layout
                icon
              }
              maxColumns
              anchor
              header
              _rawSummary(resolveReferences: { maxDepth: 5 })
              layout
              background
              padding
              style
              container
              image {
                ...SanityImage
              }
            }
            ... on SanityPageComponent {
              _type
              name
              anchor
              header
              _rawSummary(resolveReferences: { maxDepth: 5 })
              layout
              background
              padding
              container
              category
              cards
            }
          }
        }
        ... on SanityOffering {
          _type
          id
          _id
          heroImage {
            ...SanityImage
          }
          problems {
            header
            _rawSummary(resolveReferences: { maxDepth: 5 })
            mainImage {
              ...SanityImage
            }
          }
        }
      }
      compare {
        _type
        _rawCrDescription(resolveReferences: { maxDepth: 5 })
        competitor
        _rawCompetitorDescription(resolveReferences: { maxDepth: 5 })
        anchor
        header
        _rawSummary(resolveReferences: { maxDepth: 5 })
        background
        padding
        container
        table {
          label
          url
          native
          compare
          isHeader
          icon
          featured
        }
      }
      alternatives {
        ... on SanityCompetitor {
          _type
          title
          slug {
            current
          }
          _rawExcerpt(resolveReferences: { maxDepth: 5 })
        }
        ... on SanityPage {
          _type
          title
          slug {
            current
          }
          _rawExcerpt(resolveReferences: { maxDepth: 5 })
        }
      }
      categories {
        _id
        title
        slug {
          current
        }
      }
      faqs {
        question
        _rawAnswer(resolveReferences: { maxDepth: 5 })
        _key
      }
      problems {
        header
        _rawSummary(resolveReferences: { maxDepth: 5 })
        mainImage {
          ...SanityImage
        }
      }
      _rawSolutionsIntro(resolveReferences: { maxDepth: 5 })
      solutions {
        header
        _rawSummary(resolveReferences: { maxDepth: 5 })
        mainImage {
          ...SanityImage
        }
      }
    }
    stories: allSanityStory(
      sort: { fields: [orderRank], order: ASC }
      filter: {
        status: { eq: "published" }
        slug: { current: { ne: null } }
        statistics: { elemMatch: { number: { ne: null } } }
      }
    ) {
      edges {
        node {
          ...cardNodeStory
          company {
            mainImageMonoSolid {
              ...SanityImageSimple
            }
          }
        }
      }
    }
    chatAgent: sanityAgent(_id: { eq: "9984d4fd-97ce-4692-abba-3f2f6729ffdd" }) {
      ...Agent
    }
  }
`;

const CompetitorTemplate = ({ data }) => (
  <LayoutContainer doc={{ ...data.doc, backgroundColor: "lightGreen" }} hideNav={true}>
    <PageHeadMeta title={data.doc.title} doc={data.doc} />
    {data.doc && (
      <CompetitorPage {...data.doc} stories={mapEdgesToNodes(data.stories)} chatAgent={data.chatAgent} />
    )}
  </LayoutContainer>
);

export default CompetitorTemplate;

export const CompetitorPage = ({
  _rawHero,
  title,
  compare,
  _rawExcerpt,
  faqs,
  stories = [],
  solutions = [],
  _rawSolutionsIntro,
  _rawCompareIntro,
  chatbotReplies,
  chatAgent
}) => {
  const site = useContext(SiteContext);

  const seenCompanies = new Set();
  const primaryStories = [];
  const extraStories = [];
  stories.forEach((s) => {
    const id = s.company?._id;
    if (id && seenCompanies.has(id)) {
      extraStories.push(s);
    } else {
      if (id) seenCompanies.add(id);
      primaryStories.push(s);
    }
  });
  const allStories = [...primaryStories, ...extraStories].slice(0, 12);
  const slides = allStories.map((s, i) => (
    <CardContext.Provider
      key={i}
      value={{
        ...s,
        hideImage: true,
        hideType: true,
        hideDate: true,
        showByline: false,
        isTall: true,
        title: false,
        hideLabels: true,
        cta: typesMap.get("story").cta,
      }}
    >
      <div
        className="max-w-screen-md mx-auto text-xl leading-relaxed"
        style={{ minHeight: 380 }}
      >
        <Card />
      </div>
    </CardContext.Provider>
  ));
  const companies = allStories.map((s) => s.company).filter(Boolean);

  const chatbotFirstMessage = `Why the curiosity with ${compare.competitor} or Common Room?`;

  return (
    <>
      <SectionWrapper
        noHeaders={true}
        name="header"
        container={PAGE_WIDTH}
        padding="top"
      >
        <div className="grid md:grid-cols-3/5-2/5 gap-24 items-start mb-12">
          <div className="mt-12">
            <EyebrowPill>
              {title}
            </EyebrowPill>
            <h1 className="text-7xl sm:text-9xl font-semibold tracking-tight my-8">
              <PortableText blocks={_rawHero} />
            </h1>
            <PortableText
              blocks={_rawExcerpt}
              className="text-xl md:text-2xl text-secondary leading-normal md:leading-relaxed"
            />
            <a href="#compare" className="inline-block mt-8 text-link font-medium text-lg hover:text-black">
              See full comparison
              <FontAwesomeIcon icon={faArrowDown} className="ml-2 max-h-4" />
            </a>
          </div>
          <div className="border border-dark-20 rounded-xl bg-dark-5 p-6 pt-4">
            <p className="text-secondary mb-3">
              Get a personalized team <span className="font-medium">demo</span>.
            </p>
            <DemoUX skipIcons={true} />
          </div>
        </div>
      </SectionWrapper>

      <SectionWrapper container={PAGE_WIDTH} padding="none">
        <PillHeader header="Trusted by go-to-market-teams at" />
      </SectionWrapper>
      <SectionWrapper container="full" padding="tight">
        <LogoCarousel />
      </SectionWrapper>

      <SectionWrapper container={PAGE_WIDTH} background="white" padding="top">
        <PortableText blocks={_rawCompareIntro} />
      </SectionWrapper>

      <SectionWrapper container={PAGE_WIDTH} background="white" anchor="compare">
        <ComparisonTable section={compare} />
        <div className="mt-16 flex justify-center items-center gap-6">
          <p className="text-lg font-medium">Lets get started.</p>
          <Button href="#top" text={DEMO_PHRASE} bgColor="bg-black" isBig={true} />
        </div>
      </SectionWrapper>

      {solutions.length > 0 && (
        <SectionWrapper header={site.token("offeringsBetter")} container={PAGE_WIDTH} background="white">
          <SolutionsSections
            intro={_rawSolutionsIntro}
            solutions={solutions}
          />
        </SectionWrapper>
      )}

      <SectionWrapper
        container={PAGE_WIDTH}
        header="Hear it from our customers"
        layout="centered"
        padding="top"
      >
        {slides.length > 0 && (
          <Carousel slides={slides} companies={companies} skipButtons={true} />
        )}
      </SectionWrapper>

      <SectionWrapper container="thin" anchor="faqs" header="Common questions" layout="centered">
        <div className="max-w-screen-md">
          {faqs.map((faq, i) => (
            <Expandable
              key={i}
              header={faq.question}
              answer={faq._rawAnswer ? toPlainText(faq._rawAnswer) : null}
            >
              <PortableText blocks={faq._rawAnswer} />
            </Expandable>
          ))}
        </div>
        <div className="mt-24 flex justify-center items-center gap-6">
          <p className="text-lg font-medium">Lets get started.</p>
          <Button href="#top" text={DEMO_PHRASE} bgColor="bg-black" isBig={true} />
        </div>
      </SectionWrapper>

      {
        site.isActive("offeringChatbot") && (
          <SectionWrapper padding="bottom" container="thin" anchor="chat" header="Chatbot">
            <Chatbot
              // CMS default (from agent page)
              copilotID={DEFAULT_COPILOT}
              workflowID={DEFAULT_WORKFLOW}
              agent={{
                ...chatAgent,
                role: `Embed on Common Room website! Page title: ${title}. Competitor is ${compare.competitor}, info on them: ${toPlainText(compare._rawCompetitorDescription)}. Our offering: ${toPlainText(compare._rawCrDescription)}.`,
                questions: chatbotReplies,
              }}
              firstMessage={chatbotFirstMessage || DEFAULT_FIRST_MESSAGE}
              textScroll={true}
              minimal={true}
              skipAutoFocus={true}
            />
          </SectionWrapper>
        )
      }
    </>
  );
};
